import {
  Avatar,
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useEffect, useState } from "react";
import { useUserContext, useUserContextDispatch } from "../../../../hooks/userContext";
import { useUserAvatar } from "../../../../hooks/useUserAvatar";
import { useNavigate } from "react-router";
import { Pages, isAdminUser } from "../../../../utils";
import { PlanKey } from "../../../../pages/registration/homeowner-plan-selection-fieldset";
import { RoleSelector } from "../../../role-selector";
import "./styles.scss";

export const DashboardTopbar = () => {
  const { user } = useUserContext();
  const { avatarUrl, initials } = useUserAvatar();
  const { logoutUserAccount } = useUserContextDispatch();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();
  const [roleSelectorOpen, setRoleSelectorOpen] = useState(false);

  const [breadcrumb, setBreadcrumb] = useState("Dashboard");

  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 600);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const isAdmin = isAdminUser(user);
  const isDesigner = user?.accountType === "Designer";
  const isHomeowner = user?.accountType === "Homeowner";

  // Get user's subscription plan for homeowners
  const rawSubscription = typeof user?.subscription === 'string' 
    ? user.subscription 
    : (user?.subscription as any)?.homeownerValue ||
      (user?.subscription as any)?.designerValue ||
      (user?.subscription as any)?.value ||
      (user?.subscription as any)?.tier ||
      'basic';
  
  // Map GraphQL enum values to PlanKey type
  const mapSubscriptionToPlanKey = (sub: string): PlanKey => {
    if (sub === 'Free' || sub === 'basic') return 'basic';
    if (sub === 'GuildMonthly' || sub === 'Guild') return 'Guild';
    if (sub === 'GuildAnnually' || sub === 'GuildPlus') return 'GuildPlus';
    if (sub === 'StyleFileMonthly' || sub === 'StyleFileAnnually') return 'Guild';
    return 'basic';
  };
  
  const userPlan: PlanKey = mapSubscriptionToPlanKey(rawSubscription);
  const isBasicPlan = isHomeowner && userPlan === 'basic';
  const isPremiumHomeowner = isHomeowner && (userPlan === 'Guild' || userPlan === 'GuildPlus');

  const userRole = isAdmin ? "Administrator" : isDesigner ? "Designer" : "Homeowner";

  // Simplified menu for Basic plan homeowners and premium homeowners
  const menuLinks = isBasicPlan
    ? [{ label: "Manage Account", to: Pages.MY_ACCOUNT }]
    : isPremiumHomeowner
      ? [{ label: "Manage Account", to: Pages.MY_ACCOUNT }]
      : isAdmin
        ? [
            { label: "Manage Account", to: Pages.MY_ACCOUNT },
            { label: "Admin Dashboard", to: Pages.ADMIN_DASHBOARD },
          ]
        : isDesigner
          ? [
              { label: "My Application", to: Pages.DESIGNER_APPLICATION },
              { label: "Manage Account", to: Pages.MY_ACCOUNT },
            ]
          : [{ label: "Manage Account", to: Pages.MY_ACCOUNT }];

  const updateBreadcrumb = (path: string) => {
    setBreadcrumb(`Dashboard / ${path}`);
  };

  const handleMenuItemClick = (link: { label: string; to: string }) => {
    updateBreadcrumb(link.label);
    navigate(link.to);
    handleClose();
  };

  return (
    <Box className="dashboard-topbar">
      <Typography variant="body2" color="text.secondary">
        {breadcrumb}
      </Typography>

      <Box className="dashboard-topbar-user">
        <IconButton onClick={handleMenuClick} className="user-button">
          <Box display="flex" alignItems="center" gap={1}>
            <Avatar
              src={avatarUrl}
              sx={{ width: isMobile ? 32 : 40, height: isMobile ? 32 : 40 }}
            >
              {(initials ?? user?.firstName?.[0] ?? "").toUpperCase()}
            </Avatar>
            <Typography className="user-name">
              {user?.firstName} {user?.lastName?.charAt(0)}.
            </Typography>
            {isMobile && <ExpandMoreIcon sx={{ color: '#000', fontSize: 20 }} />}
          </Box>
        </IconButton>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          PaperProps={{ className: "dashboard-topbar-menu" }}
        >
          <Box className="menu-header">
            <Typography className="menu-title">Account</Typography>
            <Typography className="menu-name">
              {user?.firstName} {user?.lastName}
            </Typography>
            <Typography className="menu-role">
              {userRole}
            </Typography>

            {/* View As Role option - only for admins */}
            {isAdmin && (
              <MenuItem
                onClick={() => {
                  handleClose();
                  setRoleSelectorOpen(true);
                }}
              >
                <ListItemIcon>
                  <VisibilityIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>View As Role</ListItemText>
              </MenuItem>
            )}

            <MenuItem
              className="back-to-website"
              onClick={() => {
                handleClose();
                window.open(Pages.HOME, "_blank");
              }}
            >
              Back to Website
              <OpenInNewIcon fontSize="small" />
            </MenuItem>
          </Box>

          <Divider />

          {menuLinks.map((link) => (
            <MenuItem
              key={link.label}
              onClick={() => handleMenuItemClick(link)}
            >
              {link.label}
            </MenuItem>
          ))}

          <MenuItem
            onClick={() => {
              handleClose();
              logoutUserAccount();
            }}
          >
            Sign Out
          </MenuItem>
        </Menu>

        {/* Role Selector Modal */}
        <RoleSelector
          open={roleSelectorOpen}
          onClose={() => setRoleSelectorOpen(false)}
        />
      </Box>
    </Box>
  );
};
